import classNames from 'classnames';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router';

import {
	ColorKeys,
	ContentEditors,
	PageModule,
	PageModuleType,
	Session,
	TextOverrides
} from '../../../../../../types/working-model';
import { updatePageModule } from '../../../../../../store/actions/admin/create-event/session';
import { updateWorkingEvent } from '../../../../../../store/actions/admin/create-event';
import EditableTextSlim from "../../../../../general-ui/editable-text/editable-text-slim";
import { getDefaultModuleAlignment, getTextValue, updateTranslateKey } from '../../../../../../utils/utils';
import { EContentColorOverrideNames } from '../../../../../general-ui/edit-section-colors/edit-section-colors-modal';
import { useThunkDispatch, useTypedSelector } from '../../../../../../store/reducers/use-typed-selector';
import { ChecklistTitles } from '../../../../../../store/reducers/admin/assistant';
import { addChecklistItem } from '../../../../../../store/actions/authentication';
import { ParamsProps } from '../../../../../live-event/live-event';
import Controls from '../../session-module-controls/controls';
import { getStylingOverrides } from '../../../../../live-event/utils';
import EditableTextDraftQuill from '../../../../../general-ui/editable-text/editable-text-draft-quill';
import { useTranslate } from '../../../../../../i18n/useTranslationModules';
import useTranslationRoute from '../../../../../live-event/hooks/use-translation-route';
import '../../../../../live-event/session/session-modules/description/description.scss';
import './description.scss';
import { useEffect, useState } from 'react';

interface Props {
	page_module: PageModule;
	template: string;
}

export default function Description({ page_module, template }: Props): JSX.Element | null {

	const workingEvent = useTypedSelector(state => state.CreateEventReducer.workingEvent);
	const workingSession = useTypedSelector(state => state.CreateSessionReducer.workingSession);
	const token = useTypedSelector(state => state.AuthReducer.token);
	const user = useTypedSelector(state => state.AuthReducer.user);
	const contentEditor = useTypedSelector(state => state.CreateEventReducer.workingEvent?.settings?.content_editor);

	const workingEventSession = workingEvent?.sessions.find((s: Session) => s.session === workingSession?.session);
	const _descriptionModule = workingEventSession?.modules.find((m: PageModule) => m.type === PageModuleType.description && m.id === page_module.id);

	const [descriptionModule, setDescriptionModule] = useState<PageModule | undefined>(_descriptionModule);

	// updating description in quill editor (v1 translations) doesn't update descriptionModule, but it does update page_module,
	// so we force update here
	useEffect(() => {
		if (page_module?.id === _descriptionModule?.id) {
			setDescriptionModule(page_module);
		}
	}, [_descriptionModule, page_module]);

	const descriptionModuleHeading = descriptionModule?.content?.custom_heading;
	const descriptionModuleTitle = descriptionModule?.content?.title;
	const descriptionModuleDescriptionStringObj = descriptionModule?.content?.description;

	const dispatch = useDispatch();
	const thunkDispatch = useThunkDispatch();

	const { language } = useParams() as ParamsProps;
	const namespace = useTranslationRoute();
	const { t } = useTranslate(namespace);

	const content = page_module?.content;

	const styling_overrides = page_module?.styling_overrides;
	const { backgroundColor, backgroundImg } = getStylingOverrides(styling_overrides);

	const { custom_heading, description, title } = getTextValue(content, language);

	function handleTextChange(key: string) {

		return (text: string) => {
			if (!workingSession) return;

			const updatedModule = {
				translateString: page_module?.content?.[key],
				input: text,
				baseLanguage: workingSession.default_language,
				language
			};

			//UPDATE WORKING EVENT SINCE THE SESSION PREVIEW - EVENT TAB (SINGLE SESSION EVENT) OUTSIDE OF SESSION ITSELF USED MODULES IN WORKING EVENT TO RENDER
			if (workingEvent && workingEvent.sessions.length === 1) {
				const _workingEvent = {
					...workingEvent,
					sessions: workingEvent.sessions.map(s => {
						if (s.session === workingEventSession?.session) {
							const _session: Session = {
								...s,
								modules: s.modules.map(m => {
									if (m.id === page_module.id) {
										const _page_module: PageModule = {
											...m,
											content: {
												...m.content,
												[key]: updateTranslateKey(updatedModule),
											},
											...(key === 'description' && { is_edited: true }),
										};
										return _page_module;
									}
									return m;
								})
							};
							return _session;
						}
						return s;
					})
				};

				if (token) {
					thunkDispatch(updateWorkingEvent(_workingEvent, token));
				}
			}

			dispatch(
				updatePageModule({
					...page_module,
					content: {
						...content,
						[key]: updateTranslateKey(updatedModule),
					},
					...(key === 'description' && { is_edited: true }),
				})
			);

			// ASSISTANT CHECKLIST ITEM: complete users checklist item for updating section
			if (
				user && token &&
				!user.onboarded.includes(ChecklistTitles.editSection)
			) dispatch(addChecklistItem(token, user.id, user.onboarded, ChecklistTitles.editSection));
		};
	}

	function handleStyleOverrideChange(key: string) {
		return (overrides: TextOverrides) => {
			const updatedOverrides = {
				...styling_overrides,
				content: {
					...styling_overrides?.content,
					[key]: overrides
				}
			};
			dispatch(
				updatePageModule({
					...page_module,
					styling_overrides: updatedOverrides
				})
			);

			// ASSISTANT CHECKLIST ITEM: complete users checklist item for updating section
			if (
				user && token &&
				!user.onboarded.includes(ChecklistTitles.editSection)
			) dispatch(addChecklistItem(token, user.id, user.onboarded, ChecklistTitles.editSection));
		};
	}

	return page_module.is_on && (!workingSession?.layout?.withMenu || page_module) ? (
		<div
			className={classNames(
				"editor-wrapper description",
				backgroundColor,
				template
			)}
			style={backgroundImg}
		>
			<Controls
				page_module={page_module}
				openEditModal={() => ({})}
				display={{ editColors: true, changeSectionPosition: true }}
			/>
			<div className="container" style={backgroundImg}>
				<EditableTextSlim
					tagType="h5"
					className="evt-heading-5 custom-title"
					value={t(`${namespace}:description.${page_module.id}.heading`, t(`${namespace}:description.heading`, descriptionModuleHeading?.[language] ?? custom_heading))}
					onChange={handleTextChange('custom_heading')}
					defaultColor={ColorKeys.bodyTextColor}
					onOverrideChange={handleStyleOverrideChange('custom_heading')}
					elementName={EContentColorOverrideNames.custom_heading}
					colorOverride={styling_overrides?.content?.custom_heading?.color}
				/>
				<div className="description-content">
					<EditableTextSlim
						tagType="h3"
						className="evt-heading-3 title"
						value={t(`${namespace}:description.${page_module.id}.title`, t(`${namespace}:description.title`, descriptionModuleTitle?.[language] ?? title))}
						onChange={handleTextChange('title')}
						defaultColor={ColorKeys.bodyTextColor}
						onOverrideChange={handleStyleOverrideChange('title')}
						elementName={EContentColorOverrideNames.title}
						colorOverride={styling_overrides?.content?.title?.color}
					/>
					<EditableTextDraftQuill
						className="evt-body-text-1 description"
						value={t(`${namespace}:description.${page_module.id}.description`, t(`${namespace}:description.description`, descriptionModuleDescriptionStringObj?.[language] ?? description))}
						onChange={handleTextChange('description')}
						defaultColor={ColorKeys.bodyTextColor}
						onOverrideChange={handleStyleOverrideChange('description')}
						elementName={EContentColorOverrideNames.description}
						styleOverrides={styling_overrides?.content?.description}
						placeholder={"Enter description..."}
						useNew={contentEditor === ContentEditors.quill}
						defaultTextAlignment={getDefaultModuleAlignment(page_module, template)}
					/>
				</div>
			</div>
		</div>
	) : null;
}
